import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';

const LOGO_URL = "/logo-white.png";

const navLinks = [
  { path: '/', label: 'Home' },
  { path: '/services', label: 'Services' },
  { path: '/risk-mind', label: 'Risk Mind' },
  { path: '/industries', label: 'Industries' },
  { path: '/case-studies', label: 'Case Studies' },
  { path: '/about', label: 'About' },
  { path: '/contact', label: 'Contact' },
  { path: '/investors', label: 'Investors' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? 'nav-blur border-b border-white/5' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
            <div className="w-10 h-10 overflow-hidden rounded">
              <img
                src={LOGO_URL}
                alt="Artificial Mind"
                className="w-full h-full object-contain"
              />
            </div>
            <span className="font-heading font-bold text-lg tracking-wide text-white">
              ARTIFICIAL MIND
            </span>
          </Link>

          {/* Desktop Links */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                className={`px-4 py-2 text-sm uppercase tracking-[0.15em] font-medium transition-colors duration-200 ${
                  location.pathname === link.path
                    ? 'text-white'
                    : 'text-slate-500 hover:text-white'
                }`}
              >
                {link.label}
                {location.pathname === link.path && (
                  <motion.div
                    layoutId="nav-underline"
                    className="h-[2px] bg-brand-accent mt-1"
                    transition={{ duration: 0.3 }}
                  />
                )}
              </Link>
            ))}
          </div>

          {/* CTA */}
          <Link
            to="/contact"
            data-testid="nav-cta"
            className="hidden lg:block bg-white text-black font-bold text-xs uppercase tracking-wider px-6 py-3 hover:bg-slate-200 transition-colors duration-200"
          >
            Get Started
          </Link>

          {/* Mobile Toggle */}
          <button
            data-testid="nav-mobile-toggle"
            className="lg:hidden text-white p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden nav-blur border-t border-white/5"
          >
            <div className="px-6 py-8 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-mobile-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                  className={`block text-lg uppercase tracking-wider ${
                    location.pathname === link.path ? 'text-white' : 'text-slate-500'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/contact"
                className="inline-block mt-4 bg-white text-black font-bold text-sm uppercase tracking-wider px-8 py-3"
              >
                Get Started
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
